"use client";

import { c as _c } from "react/compiler-runtime";
import { BackButton } from "../../components/back-button";
import { ProviderCard, SelfScheduleCard } from "../../components/self-schedule-card";
import posthog from "posthog-js";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FaArrowRight, FaUser } from "react-icons/fa";
import { FiUsers } from "react-icons/fi";
import Image from "next/image";
import { Button } from "@/components/ui";
import { Skeleton } from "@/components/ui/skeleton";
import { useSelfScheduleContext } from "@/contexts/self-schedule-provider";
import { usePatientVerification } from "@/hooks/self-schedule/use-patient-verification";
import { useProviderNextAvailability } from "@/hooks/self-schedule/use-provider-next-availability";
import { formatDate, formatList, formatStaffName, formatTime } from "@/lib/format-data";
import { trackProviderSelection } from "@/lib/gtm-events";
import { cx } from "@/lib/tailwind-utils";
import { PublicProviderSummary } from "@/types/provider";
import { ProviderDetailsModal } from "./provider-details-modal";
import { FIRST_AVAILABLE_PROVIDER_ID, useSelfScheduleStore } from "@/stores/self-schedule-store";

// Provider choice is per service. A service left on auto-assign books
// first-available even when it shares the visit with choosable services.
const isAutoAssigned = (service: {
  allowIndividualProviderBooking?: boolean;
}) => service.allowIndividualProviderBooking !== true;

// On mobile the CTA is pinned above the page's floating "View Selections" pill,
// which this step keeps at a matching fixed offset. Desktop drops it back into
// the flow under the cards. Keep this button OUTSIDE the animated step
// container: the motion-* animation makes that container a containing block, so
// a fixed child would scroll with the content instead of sticking to the screen.
const CHOOSE_TIME_BUTTON_CLASS = cx("motion-preset-slide-up", "fixed bottom-20 left-1/2 z-40 -translate-x-1/2", "text-lg shadow-md", "md:static md:mt-8 md:translate-x-0 md:shadow-none");

// Leaves room for the pinned CTA and the pill stacked below it so the last card
// stays readable and tappable.
const MOBILE_CTA_CLEARANCE = "pb-32 md:pb-0";
const nextAvailableCaption = (start: string | undefined, isBookingEvent: boolean) => start ? `Next available ${formatDate(start, {
  weekday: "short",
  month: "short",
  day: "numeric"
})} at ${formatTime(start)}` : isBookingEvent ? "No openings during this event" : "No openings in the next 90 days";
const SingleProviderIntro = t0 => {
  const $ = _c(13);
  const {
    provider
  } = t0;
  let t1;
  if ($[0] !== provider) {
    t1 = formatStaffName(provider);
    $[0] = provider;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const providerFullName = t1;
  let t2;
  if ($[2] !== provider.profileImage || $[3] !== providerFullName) {
    t2 = provider.profileImage && <Image src={provider.profileImage} alt={providerFullName} width={400} height={400} className="aspect-square w-full max-w-xs rounded-xl object-cover" />;
    $[2] = provider.profileImage;
    $[3] = providerFullName;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== providerFullName) {
    t3 = <div><p className="text-h3 text-foreground">{providerFullName}</p></div>;
    $[5] = providerFullName;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== provider.bio) {
    t4 = provider.bio && <p className="whitespace-pre-wrap text-body text-foreground-secondary">{provider.bio}</p>;
    $[7] = provider.bio;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t2 || $[10] !== t3 || $[11] !== t4) {
    t5 = <div className="motion-preset-slide-right-sm relative flex flex-col gap-4 rounded-xl">{t2}{t3}{t4}</div>;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  return t5;
};

/**
 * Appointment review for selections that require more than one provider group.
 * Each group can be assigned a specific provider or left as "First available".
 * The review is complete only when the patient clicks Choose a time; unresolved
 * groups are kept as first-available preferences. Services in a group are
 * performed back-to-back, and first-available preferences are resolved to a
 * concrete free provider when the appointment is booked.
 */
const MultiProviderPreferences = t0 => {
  const $ = _c(58);
  const {
    className
  } = t0;
  const {
    providerGroups,
    usesPerOccurrenceProviderSelection,
    selectedServices,
    serviceInstanceProviderIds,
    preferredProviderEntryKeys,
    setServiceInstancesProvider,
    setConfirmedAppointmentReview
  } = useSelfScheduleContext();
  const {
    patient
  } = usePatientVerification();
  const previousProviderId = patient?.mostRecentAppointmentProviderId;
  const patientType = useSelfScheduleStore(_temp);
  const [detailsState, setDetailsState] = useState(null);
  let t1;
  if ($[0] !== selectedServices) {
    t1 = new Set((selectedServices ?? []).filter(isAutoAssigned).map(_temp2));
    $[0] = selectedServices;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const autoAssignedServiceIds = t1;
  let t2;
  if ($[2] !== autoAssignedServiceIds) {
    t2 = group => !group.serviceIds.some(id_0 => autoAssignedServiceIds.has(id_0));
    $[2] = autoAssignedServiceIds;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const groupAllowsProviderChoice = t2;
  let t3;
  let t4;
  if ($[4] !== groupAllowsProviderChoice || $[5] !== providerGroups || $[6] !== serviceInstanceProviderIds || $[7] !== setServiceInstancesProvider) {
    t3 = () => {
      for (const group_0 of providerGroups) {
        if (groupAllowsProviderChoice(group_0)) {
          continue;
        }
        const unset = group_0.instances.filter(instance => serviceInstanceProviderIds[instance.instanceId] !== FIRST_AVAILABLE_PROVIDER_ID);
        if (unset.length === 0) {
          continue;
        }
        setServiceInstancesProvider(unset.map(_temp3), FIRST_AVAILABLE_PROVIDER_ID);
      }
    };
    t4 = [providerGroups, groupAllowsProviderChoice, serviceInstanceProviderIds, setServiceInstancesProvider];
    $[4] = groupAllowsProviderChoice;
    $[5] = providerGroups;
    $[6] = serviceInstanceProviderIds;
    $[7] = setServiceInstancesProvider;
    $[8] = t3;
    $[9] = t4;
  } else {
    t3 = $[8];
    t4 = $[9];
  }
  useEffect(t3, t4);
  let t5;
  if ($[10] !== serviceInstanceProviderIds) {
    t5 = group_1 => {
      const providerId = serviceInstanceProviderIds[group_1.instances[0]?.instanceId] ?? null;
      if (providerId === null || providerId !== FIRST_AVAILABLE_PROVIDER_ID && !group_1.eligibleProviderIds.includes(providerId)) {
        return null;
      }
      return group_1.instances.every(instance_1 => serviceInstanceProviderIds[instance_1.instanceId] === providerId) ? providerId : null;
    };
    $[10] = serviceInstanceProviderIds;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const groupProviderIdFor = t5;
  let t6;
  if ($[12] !== patientType || $[13] !== preferredProviderEntryKeys || $[14] !== serviceInstanceProviderIds || $[15] !== setServiceInstancesProvider) {
    t6 = (group_2, providerId_0) => {
      const isNoOp = group_2.instances.every(instance_2 => serviceInstanceProviderIds[instance_2.instanceId] === providerId_0);
      if (isNoOp && !group_2.instances.some(instance_3 => preferredProviderEntryKeys?.[instance_3.instanceId])) {
        return;
      }
      const isFirstAvailable = providerId_0 === FIRST_AVAILABLE_PROVIDER_ID;
      const provider = group_2.eligibleProviders.find(candidate => candidate.id === providerId_0);
      const providerName = isFirstAvailable ? "First available" : provider ? formatStaffName(provider) : undefined;
      const analyticsContext = {
        provider_group_key: group_2.key,
        provider_group_service_ids: group_2.serviceIds,
        provider_group_service_names: group_2.serviceNames,
        is_first_available: isFirstAvailable
      };
      posthog.capture("self_schedule_provider_selected", {
        provider_id: providerId_0,
        provider_name: providerName,
        ...analyticsContext
      });
      trackProviderSelection(providerId_0, providerName, patientType, analyticsContext);
      setServiceInstancesProvider(group_2.instances.map(_temp4), providerId_0);
    };
    $[12] = patientType;
    $[13] = preferredProviderEntryKeys;
    $[14] = serviceInstanceProviderIds;
    $[15] = setServiceInstancesProvider;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const assignGroupProvider = t6;
  let t7;
  if ($[17] !== providerGroups) {
    t7 = providerGroups.some(_temp5);
    $[17] = providerGroups;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const hasUnbookableGroup = t7;
  let t8;
  if ($[19] !== assignGroupProvider || $[20] !== groupProviderIdFor || $[21] !== hasUnbookableGroup || $[22] !== providerGroups || $[23] !== setConfirmedAppointmentReview) {
    t8 = () => {
      if (hasUnbookableGroup) {
        return;
      }
      for (const group_4 of providerGroups) {
        if (!groupProviderIdFor(group_4)) {
          assignGroupProvider(group_4, FIRST_AVAILABLE_PROVIDER_ID);
        }
      }
      setConfirmedAppointmentReview(true);
    };
    $[19] = assignGroupProvider;
    $[20] = groupProviderIdFor;
    $[21] = hasUnbookableGroup;
    $[22] = providerGroups;
    $[23] = setConfirmedAppointmentReview;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  const continueToTimePicker = t8;
  let t9;
  if ($[25] !== className) {
    t9 = cx("motion-scale-in-105 motion-opacity-in-85", MOBILE_CTA_CLEARANCE, className);
    $[25] = className;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <div className="mb-6"><h1 className="mb-2 text-h1 text-foreground">Review your appointment</h1><p className="text-foreground-secondary">Confirm your service details and choose provider preferences before selecting a time. Add-ons will be handled with the main service.</p></div>;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== assignGroupProvider || $[29] !== groupAllowsProviderChoice || $[30] !== groupProviderIdFor || $[31] !== previousProviderId || $[32] !== providerGroups || $[33] !== usesPerOccurrenceProviderSelection) {
    t11 = providerGroups.map((group_5, groupIndex) => {
      const groupProviderId = groupProviderIdFor(group_5);
      const isFirstAvailableSelected = groupProviderId === null || groupProviderId === FIRST_AVAILABLE_PROVIDER_ID;
      return <div key={group_5.key}><h2 className="mb-3 text-h3 text-foreground">Provider for {formatList(group_5.serviceNames)}<span>{usesPerOccurrenceProviderSelection && providerGroups.some(other => other.key !== group_5.key && other.serviceIds[0] === group_5.serviceIds[0]) ? ` (selection ${groupIndex + 1})` : ""}</span></h2>{group_5.eligibleProviders.length === 0 ? <p className="text-body text-status-error-text">No provider is available for this service at the selected location. Please go back and adjust your selection.</p> : !groupAllowsProviderChoice(group_5) ? <p className="text-body text-foreground-secondary">We'll match you with an available provider for this service.</p> : <div className="grid grid-cols-1 gap-4 md:grid-cols-2"><SelfScheduleCard item={{
            id: FIRST_AVAILABLE_PROVIDER_ID,
            name: "First available",
            subtitle: "We'll match you with an available provider for this service",
            type: "provider",
            isPopular: false,
            image: <FiUsers className="h-12 w-12 flex-shrink-0 px-3 text-primary/80" />
          }} isSelected={isFirstAvailableSelected} onItemClick={() => assignGroupProvider(group_5, FIRST_AVAILABLE_PROVIDER_ID)} hideInfoButton={true} className="min-h-0 overflow-visible p-4 px-5" />{group_5.eligibleProviders.map((provider_0, index) => <ProviderCard key={provider_0.id} provider={provider_0} isSelected={groupProviderId === provider_0.id} onProviderClick={providerId_1 => assignGroupProvider(group_5, providerId_1)} onInfoClick={(e, providerId_2) => {
            e.stopPropagation();
            e.preventDefault();
            const match = group_5.eligibleProviders.find(candidate_0 => candidate_0.id === providerId_2);
            if (match) {
              setDetailsState({
                groupKey: group_5.key,
                provider: match
              });
            }
          }} isPreviousAppointment={previousProviderId === provider_0.id} animationDelay={groupProviderId === provider_0.id ? undefined : (index + 1) * 150 + 50} />)}</div>}</div>;
    });
    $[28] = assignGroupProvider;
    $[29] = groupAllowsProviderChoice;
    $[30] = groupProviderIdFor;
    $[31] = previousProviderId;
    $[32] = providerGroups;
    $[33] = usesPerOccurrenceProviderSelection;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  let t12;
  if ($[35] !== t11) {
    t12 = <div className="space-y-8">{t11}</div>;
    $[35] = t11;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  let t13;
  if ($[37] !== t12 || $[38] !== t9) {
    t13 = <div data-testid="step-appointment-review-multi" className={t9}>{t10}{t12}</div>;
    $[37] = t12;
    $[38] = t9;
    $[39] = t13;
  } else {
    t13 = $[39];
  }
  let t14;
  let t15;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <span>Choose a time</span>;
    t15 = <FaArrowRight className="ml-2" />;
    $[40] = t14;
    $[41] = t15;
  } else {
    t14 = $[40];
    t15 = $[41];
  }
  let t16;
  if ($[42] !== continueToTimePicker || $[43] !== hasUnbookableGroup) {
    t16 = <Button variant="primary" className={CHOOSE_TIME_BUTTON_CLASS} onClick={continueToTimePicker} disabled={hasUnbookableGroup}>{t14}{t15}</Button>;
    $[42] = continueToTimePicker;
    $[43] = hasUnbookableGroup;
    $[44] = t16;
  } else {
    t16 = $[44];
  }
  const t17 = detailsState?.provider ?? null;
  const t18 = detailsState !== null;
  let t19;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = () => setDetailsState(null);
    $[45] = t19;
  } else {
    t19 = $[45];
  }
  let t20;
  if ($[46] !== assignGroupProvider || $[47] !== detailsState || $[48] !== providerGroups) {
    t20 = () => {
      if (detailsState) {
        const group_6 = providerGroups.find(candidate_1 => candidate_1.key === detailsState.groupKey);
        if (group_6) {
          assignGroupProvider(group_6, detailsState.provider.id);
        }
      }
      setDetailsState(null);
    };
    $[46] = assignGroupProvider;
    $[47] = detailsState;
    $[48] = providerGroups;
    $[49] = t20;
  } else {
    t20 = $[49];
  }
  let t21;
  if ($[50] !== t17 || $[51] !== t18 || $[52] !== t20) {
    t21 = <ProviderDetailsModal provider={t17} isOpen={t18} onClose={t19} onSelect={t20} />;
    $[50] = t17;
    $[51] = t18;
    $[52] = t20;
    $[53] = t21;
  } else {
    t21 = $[53];
  }
  let t22;
  if ($[54] !== t13 || $[55] !== t16 || $[56] !== t21) {
    t22 = <>{t13}{t16}{t21}</>;
    $[54] = t13;
    $[55] = t16;
    $[56] = t21;
    $[57] = t22;
  } else {
    t22 = $[57];
  }
  return t22;
};
const AppointmentReview = t0 => {
  const $ = _c(73);
  const {
    className
  } = t0;
  const [selectedProviderForDetails, setSelectedProviderForDetails] = useState(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const {
    selectedProvider,
    setSelectedProviderId,
    setConfirmedAppointmentReview,
    availableProviders,
    isMultiProviderSelection,
    usesPerOccurrenceProviderSelection,
    selectedServices,
    sortProvidersByAvailability,
    bookingEvent
  } = useSelfScheduleContext();
  const selectedProviderId = useSelfScheduleStore(_temp6);
  const {
    patient
  } = usePatientVerification();
  const previousProviderId = patient?.mostRecentAppointmentProviderId;
  let t1;
  if ($[0] !== availableProviders || $[1] !== selectedServices) {
    t1 = availableProviders && availableProviders.length > 1 && !(selectedServices ?? []).some(isAutoAssigned) ? availableProviders.map(_temp7) : [];
    $[0] = availableProviders;
    $[1] = selectedServices;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const choosableProviderIds = t1;
  const {
    nextAvailableByProviderId,
    isLoading: isNextAvailabilityLoading,
    isLoaded: isNextAvailabilityLoaded
  } = useProviderNextAvailability(choosableProviderIds);
  if (usesPerOccurrenceProviderSelection || isMultiProviderSelection) {
    let t2;
    if ($[3] !== className) {
      t2 = <MultiProviderPreferences className={className} />;
      $[3] = className;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    return t2;
  }
  if (!availableProviders) {
    return null;
  }
  let t2;
  if ($[5] !== availableProviders || $[6] !== setSelectedProviderId) {
    t2 = providerId => {
      const provider = availableProviders.find(p => p.id === providerId);
      if (!provider) {
        return;
      }
      setSelectedProviderId(provider.id, `${provider.firstName} ${provider.lastName}`);
    };
    $[5] = availableProviders;
    $[6] = setSelectedProviderId;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const handleProviderSelect = t2;
  let t3;
  if ($[8] !== setSelectedProviderId) {
    t3 = () => {
      setSelectedProviderId(FIRST_AVAILABLE_PROVIDER_ID, "First available");
    };
    $[8] = setSelectedProviderId;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const handleFirstAvailableSelect = t3;
  let t4;
  if ($[10] !== selectedServices) {
    t4 = selectedServices ?? [];
    $[10] = selectedServices;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== t4) {
    t5 = t4.some(isAutoAssigned);
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const hasAutoAssignedService = t5;
  const showSingleProviderIntro = availableProviders.length === 1 && !hasAutoAssignedService;
  let t6;
  if ($[14] !== availableProviders[0] || $[15] !== handleFirstAvailableSelect || $[16] !== handleProviderSelect || $[17] !== hasAutoAssignedService || $[18] !== selectedProviderId || $[19] !== setConfirmedAppointmentReview || $[20] !== showSingleProviderIntro) {
    t6 = () => {
      if (showSingleProviderIntro) {
        handleProviderSelect(availableProviders[0].id);
      } else {
        if (hasAutoAssignedService || selectedProviderId === null) {
          handleFirstAvailableSelect();
        }
      }
      setConfirmedAppointmentReview(true);
    };
    $[14] = availableProviders[0];
    $[15] = handleFirstAvailableSelect;
    $[16] = handleProviderSelect;
    $[17] = hasAutoAssignedService;
    $[18] = selectedProviderId;
    $[19] = setConfirmedAppointmentReview;
    $[20] = showSingleProviderIntro;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  const continueToTimePicker = t6;
  const isFirstAvailableSelected = selectedProviderId === null || selectedProviderId === FIRST_AVAILABLE_PROVIDER_ID;
  let t7;
  if ($[22] !== availableProviders) {
    t7 = (e, providerId_0) => {
      e.stopPropagation();
      e.preventDefault();
      const provider_0 = availableProviders.find(p_0 => p_0.id === providerId_0);
      if (provider_0) {
        setSelectedProviderForDetails(provider_0);
        setIsDetailsModalOpen(true);
      }
    };
    $[22] = availableProviders;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  const handleInfoClick = t7;
  let t8;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      setIsDetailsModalOpen(false);
      setSelectedProviderForDetails(null);
    };
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  const closeDetailsModal = t8;
  let t9;
  if ($[25] !== nextAvailableByProviderId) {
    t9 = providerId_1 => {
      const start = nextAvailableByProviderId.get(providerId_1);
      return start ? Date.parse(start) : Infinity;
    };
    $[25] = nextAvailableByProviderId;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  const soonestStart = t9;
  let t10;
  if ($[27] !== availableProviders || $[28] !== isNextAvailabilityLoaded || $[29] !== soonestStart || $[30] !== sortProvidersByAvailability) {
    t10 = sortProvidersByAvailability && isNextAvailabilityLoaded ? [...availableProviders].sort((a, b) => soonestStart(a.id) - soonestStart(b.id)) : availableProviders;
    $[27] = availableProviders;
    $[28] = isNextAvailabilityLoaded;
    $[29] = soonestStart;
    $[30] = sortProvidersByAvailability;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  const orderedProviders = t10;
  if (availableProviders.length === 0) {
    let t11;
    if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = <div className="py-8 text-center space-y-2"><FaUser className="mx-auto text-4xl text-primary" /><h3 className="text-h3 font-medium text-foreground">No Providers Available</h3><p className="text-muted-foreground">There are no providers at this time. Please try another service or contact us.</p><BackButton variant="primary" size="base" /></div>;
      $[32] = t11;
    } else {
      t11 = $[32];
    }
    return t11;
  }
  let t11;
  if ($[33] !== className) {
    t11 = cx("motion-scale-in-105 motion-opacity-in-85", MOBILE_CTA_CLEARANCE, className);
    $[33] = className;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  let t12;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <h1 className="mb-2 text-h1 text-foreground">Review your appointment</h1>;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  const t13 = hasAutoAssignedService ? "Confirm your service details before selecting a time. Add-ons will be handled with the main service." : "Choose a provider if you prefer, or continue with first available to see appointment times.";
  let t14;
  if ($[36] !== t13) {
    t14 = <div className="mb-6">{t12}<p className="text-foreground-secondary">{t13}</p></div>;
    $[36] = t13;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  let t15;
  if ($[38] !== availableProviders || $[39] !== bookingEvent || $[40] !== handleFirstAvailableSelect || $[41] !== handleInfoClick || $[42] !== handleProviderSelect || $[43] !== hasAutoAssignedService || $[44] !== isFirstAvailableSelected || $[45] !== isNextAvailabilityLoaded || $[46] !== isNextAvailabilityLoading || $[47] !== nextAvailableByProviderId || $[48] !== orderedProviders || $[49] !== previousProviderId || $[50] !== selectedProvider?.id || $[51] !== showSingleProviderIntro || $[52] !== sortProvidersByAvailability) {
    t15 = hasAutoAssignedService ? <p className="text-body text-foreground-secondary">We'll match you with an available provider for this appointment.</p> : showSingleProviderIntro ? <SingleProviderIntro provider={availableProviders[0]} /> : <div className="grid grid-cols-1 gap-4 md:grid-cols-2"><SelfScheduleCard item={{
        id: FIRST_AVAILABLE_PROVIDER_ID,
        name: "First available",
        subtitle: "We'll match you with the next provider with an open slot",
        type: "provider",
        isPopular: false,
        image: <FiUsers className="h-12 w-12 flex-shrink-0 px-3 text-primary/80" />
      }} isSelected={isFirstAvailableSelected} onItemClick={handleFirstAvailableSelect} hideInfoButton={true} className="min-h-0 overflow-visible p-4 px-5" />{sortProvidersByAvailability && isNextAvailabilityLoading ? availableProviders.map(_temp8) : orderedProviders.map((provider_1, index) => <ProviderCard key={provider_1.id} provider={provider_1} caption={isNextAvailabilityLoaded ? nextAvailableCaption(nextAvailableByProviderId.get(provider_1.id), !!bookingEvent) : undefined} isSelected={selectedProvider?.id === provider_1.id} onProviderClick={handleProviderSelect} onInfoClick={handleInfoClick} isPreviousAppointment={previousProviderId === provider_1.id} animationDelay={selectedProvider?.id === provider_1.id ? undefined : (index + 1) * 150 + 50} />)}</div>;
    $[38] = availableProviders;
    $[39] = bookingEvent;
    $[40] = handleFirstAvailableSelect;
    $[41] = handleInfoClick;
    $[42] = handleProviderSelect;
    $[43] = hasAutoAssignedService;
    $[44] = isFirstAvailableSelected;
    $[45] = isNextAvailabilityLoaded;
    $[46] = isNextAvailabilityLoading;
    $[47] = nextAvailableByProviderId;
    $[48] = orderedProviders;
    $[49] = previousProviderId;
    $[50] = selectedProvider?.id;
    $[51] = showSingleProviderIntro;
    $[52] = sortProvidersByAvailability;
    $[53] = t15;
  } else {
    t15 = $[53];
  }
  let t16;
  if ($[54] !== t11 || $[55] !== t14 || $[56] !== t15) {
    t16 = <div data-testid="step-appointment-review" className={t11}>{t14}{t15}</div>;
    $[54] = t11;
    $[55] = t14;
    $[56] = t15;
    $[57] = t16;
  } else {
    t16 = $[57];
  }
  let t17;
  let t18;
  if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <span>Choose a time</span>;
    t18 = <FaArrowRight className="ml-2" />;
    $[58] = t17;
    $[59] = t18;
  } else {
    t17 = $[58];
    t18 = $[59];
  }
  let t19;
  if ($[60] !== continueToTimePicker) {
    t19 = <Button variant="primary" className={CHOOSE_TIME_BUTTON_CLASS} onClick={continueToTimePicker}>{t17}{t18}</Button>;
    $[60] = continueToTimePicker;
    $[61] = t19;
  } else {
    t19 = $[61];
  }
  let t20;
  if ($[62] !== handleProviderSelect || $[63] !== selectedProviderForDetails) {
    t20 = () => {
      if (selectedProviderForDetails) {
        handleProviderSelect(selectedProviderForDetails.id);
      }
      closeDetailsModal();
    };
    $[62] = handleProviderSelect;
    $[63] = selectedProviderForDetails;
    $[64] = t20;
  } else {
    t20 = $[64];
  }
  let t21;
  if ($[65] !== isDetailsModalOpen || $[66] !== selectedProviderForDetails || $[67] !== t20) {
    t21 = <ProviderDetailsModal provider={selectedProviderForDetails} isOpen={isDetailsModalOpen} onClose={closeDetailsModal} onSelect={t20} />;
    $[65] = isDetailsModalOpen;
    $[66] = selectedProviderForDetails;
    $[67] = t20;
    $[68] = t21;
  } else {
    t21 = $[68];
  }
  let t22;
  if ($[69] !== t16 || $[70] !== t19 || $[71] !== t21) {
    t22 = <>{t16}{t19}{t21}</>;
    $[69] = t16;
    $[70] = t19;
    $[71] = t21;
    $[72] = t22;
  } else {
    t22 = $[72];
  }
  return t22;
};
export default AppointmentReview;
function _temp(state) {
  return state.patientType;
}
function _temp2(t0) {
  const {
    id
  } = t0;
  return id;
}
function _temp3(instance_0) {
  return instance_0.instanceId;
}
function _temp4(t0) {
  const {
    instanceId
  } = t0;
  return instanceId;
}
function _temp5(group_3) {
  return group_3.eligibleProviders.length === 0;
}
function _temp6(state) {
  return state.selectedProviderId;
}
function _temp7(t0) {
  const {
    id
  } = t0;
  return id;
}
function _temp8(t0) {
  const {
    id: id_0
  } = t0;
  return <Skeleton key={id_0} className="h-20 rounded-xl" />;
}